import { emptyRemoteLink, type Task } from "@app/core";
import { getServices } from "../platform.js";

/**
 * Collapse local task rows that point at the SAME remote event.
 *
 * This is the residue pass. Before the remote link became single-writer, a lost
 * `external_id` made the next pull re-import an already-known event as a brand
 * new task — so an install can be carrying pairs of rows that both claim one
 * Google event. Nothing else in the app will ever reconcile them: unlike
 * labels/topics/calendars there is no name-dedupe for tasks, so they are
 * permanent until something collapses them.
 *
 * NON-DESTRUCTIVE TO GOOGLE, deliberately. Both rows name the SAME event, so the
 * survivor keeps the link and the remote event is never touched. The loser is
 * UNLINKED FIRST and only then soft-deleted, so the outbound push sees a
 * tombstone with no remote id and issues no delete. A soft delete is also
 * recoverable from Trash.
 *
 * Rows that merely share an `ical_uid` while holding DIFFERENT `external_id`s are
 * left alone: those are two real events in Google, and picking one to delete is
 * the user's call, not ours. The pull's re-link path stops new ones appearing.
 *
 * Deterministic across devices (freshest content, then smallest id), so two
 * devices heal to the same survivor and the tombstones converge.
 *
 * Returns how many rows were collapsed.
 */
export async function reconcileDuplicateTasks(): Promise<number> {
  const { tasks } = getServices();
  const all = await tasks.listAll();

  const groups = new Map<string, Task[]>();
  for (const t of all) {
    if (t.deleted_at || !t.external_id) continue;
    const key = `${t.external_calendar_id ?? ""}\0${t.external_id}`;
    const list = groups.get(key) ?? [];
    list.push(t);
    groups.set(key, list);
  }

  const losers: Task[] = [];
  const survivorOf = new Map<string, string>(); // loser id → survivor id
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    const [survivor, ...rest] = [...list].sort(compareSurvivor);
    for (const l of rest) {
      losers.push(l);
      survivorOf.set(l.id, survivor!.id);
    }
  }
  if (losers.length === 0) return 0;

  const loserIds = new Set(losers.map((l) => l.id));
  const now = new Date().toISOString();

  // Re-point anything hanging off a loser before it becomes a tombstone, so no
  // subtask is orphaned by the collapse.
  for (const t of all) {
    if (loserIds.has(t.id) || t.deleted_at) continue;
    const parent = t.subtask_parent_id && survivorOf.get(t.subtask_parent_id);
    const ancestor = t.parent_id && survivorOf.get(t.parent_id);
    if (!parent && !ancestor) continue;
    await tasks.update({
      ...t,
      subtask_parent_id: parent || t.subtask_parent_id,
      parent_id: ancestor || t.parent_id,
      updated_at: now,
    });
  }

  for (const l of losers) {
    // Unlink FIRST: a tombstone that still names the event would make the next
    // push delete the survivor's event out from under it. The `ical_uid` is
    // KEPT: if the process dies between these two writes, the loser survives as
    // a live, unlinked row, and the uid is the only handle the pull's re-link
    // pass has to reconnect it (after which the next heal collapses it again).
    // Clearing it made that crash residue a permanent stray. Once the tombstone
    // write below lands, the uid is inert (tombstones are excluded from the
    // re-link index, and a Trash restore unlinks fully via `setRemoteLink`).
    await tasks.setRemoteLink(l.id, { ...emptyRemoteLink(), ical_uid: l.ical_uid ?? null });
    // (`update` deliberately cannot write link columns — see TASK_REMOTE_LINK_FIELDS.)
    await tasks.update({ ...l, deleted_at: now, updated_at: now });
  }
  return losers.length;
}

/** Freshest content wins; ties break on the smallest id so every device agrees. */
function compareSurvivor(a: Task, b: Task): number {
  if (a.updated_at !== b.updated_at) return a.updated_at < b.updated_at ? 1 : -1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
